import { BREAK_GLASS_EVENT, MSG_CODE } from "config/constants";
import { useContextController } from "context";
import { logout } from "context/actions";
import { useCallback, useEffect, useRef, useState } from "react";
import { IResponse, MethodTypes, ParamsTypes } from "types";

const defaultInit: RequestInit = {
  credentials: "include"
};
const unmountError = new Error(MSG_CODE.COMPONENT_UNMOUNT);
const requestCanceled = new Error(MSG_CODE.REQUEST_CANCELED);

const useFetch = <T>(method: MethodTypes, loader?: boolean) => {
  const [isLoading, setLoading] = useState(!!loader);
  const [response, setResponse] = useState<IResponse<T> | null>(null);
  const [error, setError] = useState<any | null>(null);
  const [, dispatch] = useContextController();
  const abortRef = useRef<AbortController>();

  const cancelRequest = useCallback(() => {
    abortRef.current?.abort(requestCanceled);
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort(unmountError);
    };
  }, []);

  const callFetch = useCallback(
    (input: string, init?: (RequestInit & { params?: ParamsTypes; timeout?: number }) | undefined) => {
      return new Promise<IResponse<T>>((resolve, reject) => {
        const errorHandler = (err: any) => {
          // console.error('err')
          // console.error(err)
          setResponse(null);
          if (
            process.env.NODE_ENV === "development" &&
            [MSG_CODE.COMPONENT_UNMOUNT, MSG_CODE.REQUEST_CANCELED].includes(err?.message)
          ) {
            setError(null);
            resolve(err);
          } else {
            reject(err);
            setError(err);
          }
          setLoading(false);
        };
        setLoading(true);
        let headers = undefined;
        if (method === "POST" || method === "PUT" || method === "DELETE") {
          headers = {
            Accept: "application/json",
            "Content-Type": "application/json"
          };
        }
        const params = init?.params;
        delete init?.params;
        // console.log('params')
        // console.log(params)
        abortRef.current = new AbortController();
        const timeoutMs = typeof init?.timeout === "number" ? init.timeout : undefined;
        if (init && "timeout" in init) {
          delete (init as any).timeout;
        }
        init = {
          ...defaultInit,
          method,
          headers,
          signal: abortRef.current.signal,
          ...init
        };
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        if (timeoutMs && timeoutMs > 0) {
          timeoutId = setTimeout(() => {
            abortRef.current?.abort(new Error("Request timed out"));
          }, timeoutMs);
        }
        // console.log('final fetch input')
        // console.log(input)
        const split = input.split("?");
        let url = split[0];
        const defaultParams = split[1];
        // console.log(url)

        if (params) {
          const searchParams = new URLSearchParams();
          Object.keys(params).forEach((item) => {
            const value = params[item];
            if (value === undefined || value === null) return;
            if (Array.isArray(value)) {
              value.forEach((val) => {
                if (val === undefined || val === null) return;
                if (val instanceof Date) {
                  searchParams.append(item, val.toISOString());
                } else {
                  searchParams.append(item, val + "");
                }
              });
            } else {
              if (value instanceof Date) {
                searchParams.set(item, value.toISOString());
              } else {
                searchParams.set(item, value + "");
              }
            }
          });
          url += "?" + searchParams.toString() + (defaultParams ? "&" + defaultParams : "");
        } else {
          url += defaultParams ? "?" + defaultParams : "";
        }
        // console.log('final fetch url')
        // console.log(url)
        // console.log('final fetch init')
        // console.log(init)
        fetch(url, init)
          .then((res) => {
            // console.log('res')
            // console.log(res)
            res
              .json()
              .then((resJSON: IResponse<T>) => {
                if (timeoutId) clearTimeout(timeoutId);
                setLoading(false);
                if (resJSON.success === false || res.status > 299 || res.status < 200) {
                  if (resJSON.msgCode === MSG_CODE.UNAUTHENTICATED) {
                    logout(dispatch);
                  } else if (resJSON.msgCode === MSG_CODE.FEATURE_DISABLED && typeof window !== "undefined") {
                    window.location.reload();
                  } else if (resJSON.msgCode === MSG_CODE.BREAK_GLASS_REQUIRED && typeof window !== "undefined") {
                    let siteId: string | undefined;
                    try {
                      siteId = new URL(url, window.location.origin).searchParams.get("siteId") || undefined;
                    } catch {
                      siteId = undefined;
                    }
                    window.dispatchEvent(
                      new CustomEvent(BREAK_GLASS_EVENT, { detail: siteId ? { siteId } : {} })
                    );
                  }
                  errorHandler(resJSON);
                } else {
                  setError(null);
                  setResponse(resJSON);
                  resolve(resJSON);
                }
              })
              .catch((err) => {
                if (timeoutId) clearTimeout(timeoutId);
                errorHandler(err);
              });
          })
          .catch((err) => {
            if (timeoutId) clearTimeout(timeoutId);
            errorHandler(err);
          });
      });
    },
    [dispatch, method]
  );

  return { isLoading, response, error, callFetch, cancelRequest };
};

export default useFetch;
